/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Input, Tabs, Grid } from 'antd';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useOCPPLogs } from '../../Hooks/Index';
import { useOperatorStore } from '../../store/useOperatorStore';
import {
  DebouncedSelect,
  fetchChargerList,
} from '../../components/useDebouncedSelectLocation';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ArchivedLogs from './ArchivedLogs'; // Import the new component
import WarningMessage from '../../components/WarningMessage';
import SingleChargerDrawer from '../locations/[id]/SingleChargerDrawer';
import OCPPLogsTable from '../../components/ui/OCPPLogsTable';
import { parseLogs } from '../../utils/utils';
import { resolveOcppLogsOperatorId } from '../../utils/ocppLogsNavigation';

const { useBreakpoint } = Grid;

const AUTO_REFRESH_INTERVAL = 10; // Refresh interval in seconds

const EmbeddedPage = () => {
  const { t } = useTranslation('Ocpp-logs');
  const location = useLocation();
  const navCharger = location.state?.charger;
  const navOperatorId = location.state?.operatorId;
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const queryChargerId = queryParams.get('ChargerId');
  const queryChargerName = queryParams.get('chargerName');
  const queryOperatorId =
    queryParams.get('operatorId')?.trim() || undefined;

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const initialChargerFromQuery = () => {
    if (!queryChargerId || !queryChargerName) return null;
    let label = queryChargerName;
    try {
      label = decodeURIComponent(queryChargerName).trim();
    } catch {
      label = queryChargerName.trim();
    }
    return { label, value: queryChargerId };
  };

  const [charger, setCharger] = useState(() =>
    navCharger?.id
      ? { label: navCharger.name, value: navCharger.id }
      : initialChargerFromQuery()
  );
  const [numberOfLines, setNumberOfLines] = useState(100);
  const { operatorId } = useOperatorStore();
  const effectiveOperatorId =
    navOperatorId ??
    queryOperatorId ??
    resolveOcppLogsOperatorId(operatorId, charger);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(
    AUTO_REFRESH_INTERVAL
  );
  const [searchText, setSearchText] = useState('');
  const tableRef = useRef(null);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Responsive columns
  const responsiveColumns = [
    {
      title: t('timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: isMobile ? 120 : 180,
      align: 'center',
      className: 'whitespace-nowrap',
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
    },
    {
      title: t('messageDetails'),
      dataIndex: 'messageDetails',
      key: 'messageDetails',
      render: (text, record) => (
        <pre
          className={`whitespace-pre-wrap ${isMobile ? 'text-xs' : 'text-sm'} p-2 rounded overflow-auto max-h-40 transition-all ${
            record.isNew ? 'bg-yellow-200 animate-pulse' : 'bg-gray-100'
          }`}
        >
          {text}
        </pre>
      ),
    },
  ];

  const { data, isLoading, error, refetch } = useOCPPLogs(
    charger?.value || charger,
    numberOfLines,
    {
      enabled: !!(charger?.value || charger) 
    }
  );
  const logs = useMemo(
    () => parseLogs(data),
    [data]
  );

  const filteredLogs = useMemo(() => {
    if (!searchText) return logs;
    return logs.filter((log) =>
      log.messageDetails
        ?.toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [logs, searchText]);



  useEffect(() => {
    if (navCharger?.id) {
      setCharger({
        label: navCharger.name ?? '',
        value: navCharger.id,
      });
    } else if (queryChargerId && queryChargerName) {
      let label = queryChargerName;
      try {
        label = decodeURIComponent(queryChargerName).trim();
      } catch {
        label = queryChargerName.trim();
      }
      setCharger({ label, value: queryChargerId });
    } else {
      setCharger(null);
    }
  }, [
    navCharger?.id,
    navCharger?.name,
    queryChargerId,
    queryChargerName,
  ]);

  // Auto-Refresh with Countdown Timer
  useEffect(() => {
    let refreshInterval;
    let countdownInterval;

    if (autoRefresh) {
      // Countdown timer
      countdownInterval = setInterval(() => {
        setRefreshCountdown((prev) =>
          prev > 1 ? prev - 1 : AUTO_REFRESH_INTERVAL
        );
      }, 1000);

      // Trigger refresh at the interval
      refreshInterval = setInterval(() => {
        refetch();
        setRefreshCountdown(AUTO_REFRESH_INTERVAL); // Reset countdown after refresh
      }, AUTO_REFRESH_INTERVAL * 1000);
    }

    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [autoRefresh]);

  if (!effectiveOperatorId) {
    return <WarningMessage />;
  }

  // Extracted Table rendering logic
  let tableContent = null;
  if (!charger) {
    tableContent = (
      <OCPPLogsTable
        columns={responsiveColumns}
        dataSource={[]}
        loading={false}
        error={null}
        emptyMessage={t('selectChargerToViewLogs')}
      />
    );
  } else if (isLoading) {
    tableContent = (
      <OCPPLogsTable
        columns={responsiveColumns}
        dataSource={[]}
        loading={true}
        error={null}
      />
    );
  } else if (error) {
    tableContent = (
      <OCPPLogsTable
        columns={responsiveColumns}
        dataSource={[]}
        loading={false}
        error={`${error?.response?.data?.message ?? t('errorLoadingLogs')} ${t('chargerNeverConnected')}`}
      />
    );
  } else if (charger) {
    tableContent = (
      <OCPPLogsTable
        columns={responsiveColumns}
        dataSource={filteredLogs}
        loading={false}
        error={null}
        tableRef={tableRef}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* SingleChargerDrawer for viewing charger details */}
      <SingleChargerDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedCharger={charger?.value}
      />
      <Tabs 
        defaultActiveKey="currentLogs" 
        type="card" 
        size={isMobile ? 'small' : 'middle'}
        items={[
          {
            key: 'currentLogs',
            label: t('currentLogs'),
            children: (
              <>
                {/* Current OCPP Logs Tab */}
                <div className="space-y-4">
                  {/* Filter Row */}
                  <div className="flex flex-col lg:flex-row gap-3 lg:gap-2 lg:items-center">
                    {/* Input Fields Row */}
                    <div className="flex flex-col sm:flex-row gap-3 lg:gap-2">
                      <DebouncedSelect
                        id="chargerSelect"
                        placeholder={t('searchCharger')}
                        fetchOptions={(search, page) =>
                          fetchChargerList({
                            operatorId: effectiveOperatorId,
                            search,
                            page,
                          })
                        }
                        autoFocus={true}
                        allowClear={true}
                        onChange={(value) => setCharger(value)}
                        value={charger}
                        className="w-full sm:w-[200px] lg:w-[200px]"
                      />
                      
                      <Input
                        id="numberOfLines"
                        type="number"
                        placeholder={t('enterNumberOfLines')}
                        value={numberOfLines}
                        onChange={(e) => setNumberOfLines(Number(e.target.value))}
                        className="w-full sm:w-[120px] lg:!w-[100px]"
                      />
                      
                      <Input
                        id="searchText"
                        placeholder={t('searchMessageDetails')}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="w-full sm:w-[200px] lg:!w-[250px]"
                      />
                    </div>
                    
                    {/* Buttons Row */}
                    <div className="flex flex-row gap-2 flex-wrap lg:flex-nowrap lg:overflow-x-auto">
                      <Button
                        onClick={() => {
                          setCharger(null);
                          navigate(
                            {
                              pathname: location.pathname,
                              search: '',
                              state: {},
                            },
                            { replace: true }
                          );
                        }}
                        size="small"
                      >
                        {t('clear')}
                      </Button>
                      
                      <Button
                        disabled={!charger}
                        onClick={() => setDrawerOpen(true)}
                        type="default"
                        size="small"
                      >
                        {t('viewCharger')}
                      </Button>
                      
                      <Button
                        disabled={!charger}
                        type="primary"
                        onClick={() => refetch()}
                        size="small"
                      >
                        {t('refresh')}
                      </Button>
                      
                      <Button
                        type={autoRefresh ? 'default' : 'primary'}
                        disabled={!charger}
                        onClick={() => {
                          setAutoRefresh(!autoRefresh);
                          if (!autoRefresh) setRefreshCountdown(AUTO_REFRESH_INTERVAL);
                        }}
                        size="small"
                      >
                        {autoRefresh ? t('stopAutoRefresh') : t('enableAutoRefresh')}
                      </Button>
                      
                      <Button
                        disabled={!charger || filteredLogs.length === 0}
                        onClick={() => {
                          const text = filteredLogs
                            .filter(log => typeof log.messageDetails === 'string')
                            .map(log => `[${log.timestamp}] ${log.messageDetails}`)
                            .join('\n');
                          const blob = new Blob([text], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `ocpp-logs-${charger?.label || 'export'}.txt`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                        size="small"
                      >
                        {t('export')}
                      </Button>
                      
                      {autoRefresh && (
                        <span className="text-gray-500 text-xs whitespace-nowrap">
                          {t('autoRefreshingIn', { seconds: refreshCountdown })}
                        </span>
                      )}
                      
                      <span className="text-xs text-muted whitespace-nowrap">
                        {t('latestMessageOnTop')}
                      </span>
                    </div>
                  </div>   
                </div>
                
                <div className="mt-4 overflow-x-auto">
                  {tableContent}
                </div>
              </>
            ),
          },
          {
            key: 'archivedLogs',
            label: t('archivedLogs', 'Archived Logs'),
            children: (
              <ArchivedLogs
                key={charger?.value ?? 'no-charger'}
                charger={charger}
                operatorId={effectiveOperatorId}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default EmbeddedPage;
